import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Check } from "lucide-react";
import instagramStory from "@/assets/instagram-story.png";
import facebookStory from "@/assets/facebook-story.png";
import tiktokStory from "@/assets/tiktok-story.png";

interface MousePosition {
  x: number;
  y: number;
}

const SMMSection = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const relativeScroll = window.scrollY - sectionTop + window.innerHeight;
        setScrollY(relativeScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (imagesContainerRef.current) {
      const rect = imagesContainerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
      const y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
      setMousePosition({ x, y });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const get3DTransform = (
    baseRotation: number,
    parallaxOffset: number,
    intensity: number = 1
  ) => {
    const rotateX = -mousePosition.y * 15 * intensity;
    const rotateY = mousePosition.x * 15 * intensity;
    return `
      perspective(1000px)
      translateY(${parallaxOffset}px)
      rotateX(${rotateX}deg)
      rotateY(${rotateY}deg)
      rotate(${baseRotation}deg)
    `;
  };

  const features = [
    t("smm.home.feature1"),
    t("smm.home.feature2"),
    t("smm.home.feature3"),
    t("smm.home.feature4"),
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background overflow-hidden">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Parallax Social Media Screenshots */}
          <div 
            ref={imagesContainerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative h-[450px] md:h-[550px] cursor-pointer"
            style={{ perspective: "1000px" }}
          >
            {/* Instagram Story */}
            <div
              className="absolute left-[5%] top-[5%] w-32 h-56 md:w-40 md:h-72 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out hover:shadow-3xl"
              style={{
                transform: get3DTransform(-6, scrollY * 0.06, 1.2),
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src={instagramStory}
                alt="Instagram Story"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-white/20 pointer-events-none" />
            </div>

            {/* Facebook Story */}
            <div
              className="absolute left-[35%] top-[0%] w-28 h-48 md:w-36 md:h-64 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out hover:shadow-3xl"
              style={{
                transform: get3DTransform(4, scrollY * 0.1, 0.8),
                transformStyle: "preserve-3d",
              }}
            >
              <img
                src={facebookStory}
                alt="Facebook Story"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-white/20 pointer-events-none" />
            </div>

            {/* TikTok Story */}
            <div
              className="absolute right-[5%] top-[10%] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out hover:shadow-3xl"
              style={{
                transform: get3DTransform(8, scrollY * 0.04, 1),
                transformStyle: "preserve-3d",
                width: "9rem",
                height: "15rem",
              }}
            >
              <img
                src={tiktokStory}
                alt="TikTok"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-white/20 pointer-events-none" />
            </div>

            {/* Decorative gradient circles */}
            <div
              className="absolute left-[0%] bottom-[10%] w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 opacity-40 blur-xl transition-transform duration-500"
              style={{
                transform: `translateY(${scrollY * -0.05}px) translateX(${mousePosition.x * 20}px)`,
              }}
            />
            <div
              className="absolute right-[20%] bottom-[5%] w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 opacity-35 blur-xl transition-transform duration-500"
              style={{
                transform: `translateY(${scrollY * -0.08}px) translateX(${mousePosition.x * -15}px)`,
              }}
            />
            <div
              className="absolute left-[40%] bottom-[0%] w-14 h-14 md:w-18 md:h-18 rounded-full bg-black opacity-30 blur-xl transition-transform duration-500"
              style={{
                transform: `translateY(${scrollY * -0.06}px) translateY(${mousePosition.y * 10}px)`,
              }}
            />
          </div>

          {/* Right - Content */}
          <div className="space-y-6">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              {t("smm.label")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {t("smm.title")}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("smm.description")}
            </p>
            
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            <Button asChild size="lg" className="mt-4">
              <Link to="/marketing/smm">{t("smm.cta")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SMMSection;
